// =============================================================================
// components/markets/Sparkline.tsx — tiny SVG trend line for market rows
// (IMG_5879 structure) + the full-size Coin Detail chart via <Sparkline area>.
//
// Pure react-native-svg (already linked). No chart library, no native add-on.
// Color defaults follow the trend (success green up / error red down) but can
// be overridden. Guards: <2 points or a flat range renders a clean midline.
// =============================================================================
import React from 'react';
import Svg, { Path, Defs, LinearGradient, Stop, Circle } from 'react-native-svg';
import { colors } from '../../constants/theme';

export interface SparklineProps {
  /** The price series, oldest → newest. */
  data: number[];
  width?: number;
  height?: number;
  strokeWidth?: number;
  /** Explicit stroke color; default = green when last >= first, else red. */
  color?: string;
  /** Render a soft gradient fill under the line (used by the detail chart). */
  area?: boolean;
  /** Mark the last point with a dot (detail chart). */
  endDot?: boolean;
}

export default function Sparkline({
  data,
  width = 64,
  height = 26,
  strokeWidth = 1.75,
  color,
  area = false,
  endDot = false,
}: SparklineProps) {
  const series = Array.isArray(data) ? data.filter((n) => typeof n === 'number' && isFinite(n)) : [];
  const up = series.length >= 2 ? series[series.length - 1] >= series[0] : true;
  const stroke = color ?? (up ? colors.success : colors.error);

  // Inset so the stroke never clips at the edges.
  const pad = strokeWidth + 1;
  const w = Math.max(width - pad * 2, 1);
  const h = Math.max(height - pad * 2, 1);

  let points: Array<[number, number]>;
  if (series.length < 2) {
    points = [[pad, pad + h / 2], [pad + w, pad + h / 2]];
  } else {
    const min = Math.min(...series);
    const max = Math.max(...series);
    const range = max - min;
    points = series.map((v, i) => {
      const x = pad + (i / (series.length - 1)) * w;
      // Flat series → midline (avoid divide-by-zero jitter).
      const y = range === 0 ? pad + h / 2 : pad + (1 - (v - min) / range) * h;
      return [x, y];
    });
  }

  const line = points
    .map(([x, y], i) => `${i === 0 ? 'M' : 'L'}${x.toFixed(2)},${y.toFixed(2)}`)
    .join(' ');
  const areaPath = `${line} L${(pad + w).toFixed(2)},${(pad + h).toFixed(2)} L${pad.toFixed(2)},${(pad + h).toFixed(2)} Z`;
  const [endX, endY] = points[points.length - 1];
  // Unique-ish gradient id so multiple sparklines in one list don't collide.
  const gid = `spark-${stroke.replace(/[^a-zA-Z0-9]/g, '')}`;

  return (
    <Svg width={width} height={height} pointerEvents="none">
      {area ? (
        <>
          <Defs>
            <LinearGradient id={gid} x1="0" y1="0" x2="0" y2="1">
              <Stop offset="0" stopColor={stroke} stopOpacity={0.22} />
              <Stop offset="1" stopColor={stroke} stopOpacity={0.02} />
            </LinearGradient>
          </Defs>
          <Path d={areaPath} fill={`url(#${gid})`} />
        </>
      ) : null}
      <Path
        d={line}
        stroke={stroke}
        strokeWidth={strokeWidth}
        fill="none"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      {endDot ? <Circle cx={endX} cy={endY} r={strokeWidth + 1.5} fill={stroke} /> : null}
    </Svg>
  );
}
